import { useEffect, useState } from "react";

export interface RefRow {
    address: string;
    addressShort: string;
    volume: bigint;
    count: bigint;
    earned: bigint;
}

export interface LpStakerRow {
    address: string;
    addressShort: string;
    staked: bigint;
    lockEnd: bigint;
    multiplierBps: number;
    earned: bigint;
}

export interface PStakerRow {
    address: string;
    addressShort: string;
    staked: bigint;
    earned: bigint;
}

export interface MinterRow {
    address: string;
    addressShort: string;
    nfts: bigint;
}

export interface LeaderboardData {
    referrers: RefRow[];
    lpStakers: LpStakerRow[];
    piggyStakers: PStakerRow[];
    topMinters: MinterRow[];
    nfts: { minted: bigint; max: bigint };
    fetchedAt: number;
}

const EMPTY: LeaderboardData = {
    referrers: [],
    lpStakers: [],
    piggyStakers: [],
    topMinters: [],
    nfts: { minted: 0n, max: 5_000n },
    fetchedAt: 0,
};

// Numbers come back as strings (BigInt → JSON). Cast everything back at the
// edge. Audit fix L5: BigInt("abc") throws SyntaxError — wrap so a malformed
// payload from a compromised/MITM'd response can't crash the React tree.
const toBigint = (v: unknown): bigint => {
    try {
        if (typeof v === "bigint") return v;
        if (typeof v === "string") {
            if (!/^-?\d+$/.test(v)) return 0n;
            return BigInt(v);
        }
        if (typeof v === "number" && Number.isFinite(v)) return BigInt(Math.trunc(v));
        return 0n;
    } catch {
        return 0n;
    }
};

export function useLeaderboards(): { data: LeaderboardData; loading: boolean; error?: string } {
    const [data, setData] = useState<LeaderboardData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let active = true;
        // The leaderboards function is the heaviest (several event scans); on a
        // cold Netlify container the first hit can time out. Retry a couple
        // times before surfacing "offline" so a transient cold start doesn't
        // look broken, and keep the last good data across refreshes.
        const fetchOnce = async (attempt = 0): Promise<Response> => {
            try {
                const res = await fetch("/.netlify/functions/hall-leaderboards", {
                    signal: AbortSignal.timeout(20_000),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res;
            } catch (e) {
                if (attempt < 2) {
                    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
                    return fetchOnce(attempt + 1);
                }
                throw e;
            }
        };
        const fetchData = async () => {
            try {
                const res = await fetchOnce();
                const raw = await res.json() as Record<string, unknown>;
                if (!active) return;
                setError(undefined);
                setData({
                    referrers: ((raw.referrers ?? []) as unknown[]).map((r) => {
                        const x = r as Record<string, unknown>;
                        return {
                            address: x.address as string,
                            addressShort: x.addressShort as string,
                            volume: toBigint(x.volume),
                            count: toBigint(x.count),
                            earned: toBigint(x.earned),
                        };
                    }),
                    lpStakers: ((raw.lpStakers ?? []) as unknown[]).map((r) => {
                        const x = r as Record<string, unknown>;
                        return {
                            address: x.address as string,
                            addressShort: x.addressShort as string,
                            staked: toBigint(x.staked),
                            lockEnd: toBigint(x.lockEnd),
                            multiplierBps: Number(x.multiplierBps ?? 10_000),
                            earned: toBigint(x.earned),
                        };
                    }),
                    piggyStakers: ((raw.piggyStakers ?? []) as unknown[]).map((r) => {
                        const x = r as Record<string, unknown>;
                        return {
                            address: x.address as string,
                            addressShort: x.addressShort as string,
                            staked: toBigint(x.staked),
                            earned: toBigint(x.earned),
                        };
                    }),
                    topMinters: ((raw.topMinters ?? []) as unknown[]).map((r) => {
                        const x = r as Record<string, unknown>;
                        return {
                            address: x.address as string,
                            addressShort: x.addressShort as string,
                            nfts: toBigint(x.nfts),
                        };
                    }),
                    nfts: {
                        minted: toBigint((raw.nfts as Record<string, unknown>)?.minted),
                        max:    toBigint((raw.nfts as Record<string, unknown>)?.max) || 5_000n,
                    },
                    fetchedAt: Number(raw.fetchedAt ?? Date.now()),
                });
                setLoading(false);
            } catch (e) {
                if (!active) return;
                setError((e as Error).message);
                setLoading(false);
            }
        };
        fetchData();
        const id = setInterval(fetchData, 60_000); // refresh every minute
        return () => {
            active = false;
            clearInterval(id);
        };
    }, []);

    return { data, loading, error };
}
